/**
 * Cable Store - Zustand state management for cables
 */
import { create } from 'zustand';
import { Cable } from '../types';
import { api } from '../services/api';
import { getErrorMessage } from '../utils/errorMessages';

interface CableState {
  // State
  cables: Cable[];
  selectedCable: Cable | null;
  loading: boolean;
  error: string | null;

  // Actions
  fetchCables: (projectId: number) => Promise<void>;
  createCable: (projectId: number, data: any) => Promise<Cable>;
  updateCable: (projectId: number, cableId: number, data: Partial<Cable>) => Promise<void>;
  deleteCable: (projectId: number, cableId: number) => Promise<void>;
  setSelectedCable: (cable: Cable | null) => void;
  clearCables: () => void;
  clearError: () => void;
}

export const useCableStore = create<CableState>((set, get) => ({
  // Initial state
  cables: [],
  selectedCable: null,
  loading: false,
  error: null,

  // Fetch all cables
  fetchCables: async (projectId: number) => {
    set({ loading: true, error: null });
    try {
      const response = await api.cables.getAll(projectId);
      set({ cables: response.data, loading: false });
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Nepodarilo sa načítať káble.'), loading: false });
    }
  },

  // Create cable
  createCable: async (projectId: number, data: any) => {
    set({ loading: true, error: null });
    try {
      const response = await api.cables.create(projectId, data);
      const cable = response.data;
      set((state) => ({
        cables: [...state.cables, cable],
        loading: false,
      }));
      return cable;
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Nepodarilo sa vytvoriť kábel.'), loading: false });
      throw error;
    }
  },

  // Update cable
  updateCable: async (projectId: number, cableId: number, data: Partial<Cable>) => {
    set({ loading: true, error: null });
    try {
      console.log('🔄 Updating cable:', cableId, data);
      const response = await api.cables.update(projectId, cableId, data);
      const updated = response.data;
      console.log('✅ Cable updated:', updated);

      set((state) => {
        const newCables = state.cables.map((c) => (c.id === cableId ? updated : c));
        console.log('📊 Store updated. Old cables:', state.cables.length, 'New cables:', newCables.length);
        return {
          cables: newCables,
          selectedCable: state.selectedCable?.id === cableId ? updated : state.selectedCable,
          loading: false,
        };
      });
    } catch (error: any) {
      console.error('❌ Failed to update cable:', error);
      set({ error: getErrorMessage(error, 'Nepodarilo sa uložiť zmeny kábla.'), loading: false });
      throw error;
    }
  },

  // Delete cable
  deleteCable: async (projectId: number, cableId: number) => {
    set({ loading: true, error: null });
    try {
      await api.cables.delete(projectId, cableId);
      set((state) => ({
        cables: state.cables.filter((c) => c.id !== cableId),
        selectedCable: state.selectedCable?.id === cableId ? null : state.selectedCable,
        loading: false,
      }));
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Nepodarilo sa zmazať kábel.'), loading: false });
      throw error;
    }
  },

  // Set selected cable
  setSelectedCable: (cable) => {
    set({ selectedCable: cable });
  },

  // Clear cables
  clearCables: () => {
    set({ cables: [], selectedCable: null });
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },
}));

